import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import {
  Users,
  Loader2,
  Crown,
  Bot,
  LogOut,
  Server,
  Activity,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useServers } from "@/hooks/useApi";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";

interface MyServersProps {
  onSelectServer: (serverId: string) => void;
  onLogout: () => void;
  user: any;
}

const getUserAvatarUrl = (user: any) => {
  if (!user) return null;
  const avatar = user.avatar;
  if (!avatar) return null;
  if (typeof avatar === "string" && avatar.startsWith("http")) return avatar;
  if (user.discordId) {
    return `https://cdn.discordapp.com/avatars/${user.discordId}/${avatar}.png?size=128`;
  }
  return avatar;
};

export const MyServers = ({ onSelectServer, onLogout, user }: MyServersProps) => {
  const { t } = useI18n();
  const { data: servers = [], isLoading, error } = useServers() as {
    data: any[];
    isLoading: boolean;
    error: any;
  };
  const [botStatuses, setBotStatuses] = useState<Record<string, boolean>>({});
  const [loadingStatuses, setLoadingStatuses] = useState<Record<string, boolean>>({});
  // Mostra tutti i server per default: lo stato del bot viene verificato dopo,
  // ma non deve far sparire i server durante il caricamento o se il bot va invitato.
  const [showOnlyBotServers, setShowOnlyBotServers] = useState(false);

  // Check bot status for each server
  useEffect(() => {
    const checkBotStatuses = async () => {
      const missingServers = servers.filter((server) => !(server.id in botStatuses));
      if (missingServers.length === 0) {
        return;
      }

      setLoadingStatuses((prev) => {
        const next = { ...prev };
        for (const server of missingServers) {
          next[server.id] = true;
        }
        return next;
      });

      const { serverApi } = await import('@/lib/api');
      const settled = await Promise.allSettled(
        missingServers.map(async (server) => {
          const status = await serverApi.getBotStatus(server.id);
          return { serverId: server.id, botInServer: status?.botInServer || false };
        })
      );

      const nextStatuses: Record<string, boolean> = {};
      for (const result of settled) {
        if (result.status === 'fulfilled') {
          nextStatuses[result.value.serverId] = result.value.botInServer;
        }
      }

      setBotStatuses((prev) => ({ ...prev, ...nextStatuses }));
      setLoadingStatuses((prev) => {
        const next = { ...prev };
        for (const server of missingServers) {
          next[server.id] = false;
        }
        return next;
      });

      for (const result of settled) {
        if (result.status === 'rejected') {
          console.error('Error checking bot status:', result.reason);
        }
      }
    };

    if (servers.length > 0) {
      void checkBotStatuses();
    }
  }, [servers, botStatuses]);

  const handleInviteBot = async (guildId: string) => {
    try {
      const { serverApi } = await import('@/lib/api');
      const response = await serverApi.getBotInviteUrl();
      const inviteUrl = `${response.url}&guild_id=${guildId}`;
      window.open(inviteUrl, '_blank');
    } catch (error) {
      console.error('Error getting invite URL:', error);
      const clientId = import.meta.env.VITE_DISCORD_CLIENT_ID;
      if (clientId) {
        const inviteUrl = `https://discord.com/oauth2/authorize?client_id=${clientId}&scope=bot&permissions=8&guild_id=${guildId}`;
        window.open(inviteUrl, '_blank');
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">{t('servers.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-destructive mb-4">{t('servers.error')}</p>
          <Button onClick={() => window.location.reload()}>{t('servers.retry')}</Button>
        </div>
      </div>
    );
  }

  if (servers.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30 flex flex-col">
        {/* Header */}
        <header className="h-16 border-b border-border flex items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <div className="logo-frame w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Bot className="w-6 h-6 text-primary" />
            </div>
            <h1 className="text-xl font-bold">Community Hub</h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              {getUserAvatarUrl(user) ? (
                <img
                  src={getUserAvatarUrl(user)}
                  alt={user?.username || "User"}
                  className="w-8 h-8 rounded-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = "none";
                    const fallback = target.nextElementSibling as HTMLElement | null;
                    if (fallback) fallback.classList.remove("hidden");
                  }}
                />
              ) : null}
              <div className={cn(
                "w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-xs font-bold",
                getUserAvatarUrl(user) && "hidden"
              )}>
                {user?.username?.charAt(0).toUpperCase() || 'U'}
              </div>
              <span className="text-sm font-medium">{user?.username}</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={onLogout}
              className="gap-2"
            >
              <LogOut className="w-4 h-4" />
              {t('servers.logout')}
            </Button>
          </div>
        </header>

        {/* Empty State */}
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="text-center space-y-4 max-w-md">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center">
              <Users className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold">{t('servers.noServers')}</h2>
            <p className="text-muted-foreground">
              {t('servers.noServersDesc')}
            </p>
          </div>
        </main>
      </div>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
  };

  const isServerBotActive = (server: any) => {
    if (typeof botStatuses[server.id] === 'boolean') {
      return botStatuses[server.id];
    }
    return Boolean(server?.botInServer);
  };

  const filteredServers = showOnlyBotServers
    ? servers.filter((server) => isServerBotActive(server))
    : servers;
  const hiddenBotServersCount = servers.length - filteredServers.length;
  const activeServersCount = servers.filter((server) => isServerBotActive(server)).length;
  const totalMembersCount = servers.reduce((total, server) => total + Number(server?._count?.members || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      {/* Header */}
      <header className="h-16 border-b border-border flex items-center justify-between px-6 sticky top-0 z-10 bg-background/80 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          <div className="logo-frame w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Bot className="w-6 h-6 text-primary" />
          </div>
          <h1 className="text-xl font-bold">{t('servers.title')}</h1>
        </div>
        <div className="flex items-center gap-4">
          <Button
            variant={showOnlyBotServers ? "default" : "outline"}
            size="sm"
            onClick={() => setShowOnlyBotServers((prev) => !prev)}
            className="gap-2"
          >
            <Bot className="w-4 h-4" />
            {showOnlyBotServers ? t('servers.onlyBot') : t('servers.showAll')}
          </Button>
          <div className="flex items-center gap-3">
            {getUserAvatarUrl(user) ? (
              <img
                src={getUserAvatarUrl(user)}
                alt={user?.username || "User"}
                className="w-8 h-8 rounded-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = "none";
                  const fallback = target.nextElementSibling as HTMLElement | null;
                  if (fallback) fallback.classList.remove("hidden");
                }}
              />
            ) : null}
            <div className={cn(
              "w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-xs font-bold",
              getUserAvatarUrl(user) && "hidden"
            )}>
              {user?.username?.charAt(0).toUpperCase() || 'U'}
            </div>
            <span className="text-sm font-medium">{user?.username}</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onLogout}
            className="gap-2"
          >
            <LogOut className="w-4 h-4" />
            {t('servers.logout')}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="page-container mx-auto py-8">
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-2">
            {t('servers.welcome', { username: user?.username || 'User' })}
          </h2>
          <p className="text-muted-foreground">
            {t('servers.selectPrompt')}
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            Sono disponibili <strong className="text-foreground">{servers.length}</strong> server. Puoi entrare nella dashboard anche prima di invitare il bot.
            {showOnlyBotServers && hiddenBotServersCount > 0 ? ` ${hiddenBotServersCount} server sono nascosti dal filtro.` : ''}
          </p>
        </div>

        {/* Overview */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          <Card className="border-border/70 bg-card/80 shadow-sm">
            <CardContent className="flex items-center gap-4 p-5">
              <div className="rounded-xl bg-primary/10 p-3"><Server className="h-5 w-5 text-primary" /></div>
              <div><p className="text-sm text-muted-foreground">Server disponibili</p><p className="text-2xl font-bold">{servers.length}</p></div>
            </CardContent>
          </Card>
          <Card className="border-border/70 bg-card/80 shadow-sm">
            <CardContent className="flex items-center gap-4 p-5">
              <div className="rounded-xl bg-emerald-500/10 p-3"><Activity className="h-5 w-5 text-emerald-500" /></div>
              <div><p className="text-sm text-muted-foreground">Server con bot attivo</p><p className="text-2xl font-bold text-emerald-500">{activeServersCount}</p></div>
            </CardContent>
          </Card>
          <Card className="border-border/70 bg-card/80 shadow-sm">
            <CardContent className="flex items-center gap-4 p-5">
              <div className="rounded-xl bg-blue-500/10 p-3"><Users className="h-5 w-5 text-blue-500" /></div>
              <div><p className="text-sm text-muted-foreground">Utenti nei server</p><p className="text-2xl font-bold">{totalMembersCount.toLocaleString()}</p></div>
            </CardContent>
          </Card>
          <Card className="border-border/70 bg-card/80 shadow-sm">
            <CardContent className="flex items-center gap-4 p-5">
              <div className="rounded-xl bg-amber-500/10 p-3"><Bot className="h-5 w-5 text-amber-500" /></div>
              <div><p className="text-sm text-muted-foreground">Copertura bot</p><p className="text-2xl font-bold">{servers.length ? Math.round((activeServersCount / servers.length) * 100) : 0}%</p></div>
            </CardContent>
          </Card>
        </div>

        {/* Servers Grid */}
        {filteredServers.length === 0 && servers.length > 0 && (
          <Card className="mb-6 border-amber-500/30 bg-amber-500/5">
            <CardContent className="flex flex-col items-center gap-3 py-8 text-center">
              <Bot className="h-8 w-8 text-amber-400" />
              <p className="font-medium">Nessun server visibile con il filtro attivo</p>
              <p className="text-sm text-muted-foreground">Disattiva “Solo server con bot” per vedere tutti i server disponibili.</p>
              <Button variant="outline" onClick={() => setShowOnlyBotServers(false)}>Mostra tutti i server</Button>
            </CardContent>
          </Card>
        )}

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {filteredServers.map((server) => {
            const isBotInServer = isServerBotActive(server);
            const isStatusLoading = loadingStatuses[server.id];

            return (
              <motion.div
                key={server.id}
                variants={itemVariants}
              >
                <Card
                  className={cn(
                    "bg-card border border-border rounded-2xl shadow-sm transition-all duration-300",
                    isBotInServer && "cursor-pointer hover:shadow-lg hover:border-primary/40 hover:-translate-y-1"
                  )}
                  onClick={() => {
                    if (isBotInServer) {
                      onSelectServer(server.id);
                    }
                  }}
                >
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        {server.icon ? (
                          <img
                            src={`https://cdn.discordapp.com/icons/${server.discordId}/${server.icon}.png`}
                            alt={server.name}
                            className="w-16 h-16 rounded-2xl flex-shrink-0 object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const fallback = target.nextElementSibling as HTMLElement;
                              if (fallback) fallback.classList.remove('hidden');
                            }}
                          />
                        ) : null}
                        <div
                          className={cn(
                            "w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-3xl flex-shrink-0",
                            server.icon && "hidden"
                          )}
                        >
                          🎮
                        </div>
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-lg truncate">{server.name}</CardTitle>
                          <CardDescription className="text-sm flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {t('servers.members', { count: server._count?.members?.toLocaleString() || 0 })}
                          </CardDescription>
                        </div>
                      </div>
                      {server.premium && (
                        <div className="flex-shrink-0">
                          <Crown className="w-5 h-5 text-amber-400" />
                        </div>
                      )}
                    </div>
                  </CardHeader>

                  <CardContent className="pt-0 space-y-4">
                    {/* Active Plugins Count */}
                    {((server.pluginsTotalCount ?? 0) > 0 || (server.plugins?.length ?? 0) > 0) && (
                      <div className="flex items-center justify-between p-3 bg-primary/5 rounded-lg">
                        <span className="text-sm text-muted-foreground">Plugin Attivi</span>
                        <span className="font-semibold text-primary">
                          {(server.pluginsEnabledCount ?? server.plugins?.filter((p: any) => p.enabled).length ?? 0)}/{(server.pluginsTotalCount ?? server.plugins?.length ?? 0)}
                        </span>
                      </div>
                    )}

                    {/* Action Button */}
                    <Button
                      className={cn(
                        "w-full transition-all duration-300",
                        isBotInServer
                          ? "bg-gradient-to-r from-primary to-primary/80 text-primary-foreground hover:shadow-lg"
                          : "border border-primary text-primary hover:bg-primary/10"
                      )}
                      variant={isBotInServer ? "default" : "outline"}
                      disabled={isStatusLoading}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isBotInServer) {
                          onSelectServer(server.id);
                        } else {
                          handleInviteBot(server.discordId);
                        }
                      }}
                    >
                      {isStatusLoading ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Caricamento...
                        </span>
                      ) : isBotInServer ? (
                        "Gestisci Server"
                      ) : (
                        "Aggiungi Bot"
                      )}
                    </Button>

                    {/* Bot Status Badge */}
                    <div className="flex items-center justify-center gap-2 text-xs">
                      <div
                        className={cn(
                          "w-2 h-2 rounded-full",
                          isBotInServer ? "bg-green-500" : "bg-amber-500"
                        )}
                      />
                      <span className="text-muted-foreground">
                        {isBotInServer ? "Bot Attivo" : "Bot Non Attivo"}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>
      </main>
    </div>
  );
};
